<template>
  <div>
    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>Before tax contribution <help-icon message="Before-tax contributions, also known as concessional contributions, include salary sacrifice contributions and any contributions that you’ve claimed a tax deduction for." :is-error="preTaxError" error-message="Additional tax is paid on contributions that exceed the concessional contributions cap. These contributions will be taxed at your marginal rate, plus an interest charge. Please see our website for further info."></help-icon></label>
      </div>

      <div class="medium-4 columns">
        <slider :property="PreTaxMemberContributions" @update="setPreTaxMemberContributions($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <select @change="setPreTaxMemberContributionsFrequency($event.target.value)">
          <option value="1" :selected="PreTaxMemberContributionsFrequency.value === 1">Annually</option>
          <option value="12" :selected="PreTaxMemberContributionsFrequency.value === 12">Monthly</option>
        </select>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" filter="currencyDisplay" :property="PreTaxMemberContributions" @change="setPreTaxMemberContributions($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>After tax contribution <help-icon message="<p>After-tax contributions, also known as non-concessional contributions, are contributions made from your after-tax income. By making an after-tax contribution, you may be eligible for the Commonwealth Government co-contribution.</p>" :is-error="postTaxErrors" :error-message="postTaxErrorMessages" :html-mode="true"></help-icon></label>
      </div>

      <div class="medium-4 columns">
        <slider :property="PostTaxMemberContributions" @update="setPostTaxMemberContributions($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <select @change="setPostTaxMemberContributionsFrequency($event.target.value)">
          <option value="1" :selected="PostTaxMemberContributionsFrequency.value === 1">Annually</option>
          <option value="12" :selected="PostTaxMemberContributionsFrequency.value === 12">Monthly</option>
        </select>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" filter="currencyDisplay" :property="PostTaxMemberContributions" @change="setPostTaxMemberContributions($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>One-off contribution <help-icon message="A one-off contribution is a lump sum after-tax (also known as non-concessional) contribution made from after-tax income. By making an after-tax contribution, you may be eligible for the Commonwealth Government co-contribution. Enter how much you expect to contribute as a lump sum."></help-icon></label>
      </div>

      <div class="medium-6 columns">
        <slider :property="OneOffContribution" @update="setOneOffContribution($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" filter="currencyDisplay" :property="OneOffContribution" @change="setOneOffContribution($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>At what age do you plan to make a one off contribution? <help-icon message="Enter the age you expect to be when you make this one-off contribution."></help-icon></label>
      </div>

      <div class="medium-6 columns">
        <slider :property="OneOffContributionMadeAtAge" @update="setOneOffContributionMadeAtAge($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" :property="OneOffContributionMadeAtAge" @change="setOneOffContributionMadeAtAge($event.target.value)"></custom-input>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { EmployerType } from '@/constants';

export default {
  props: {
    modifier: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState(['inputs', 'inferred', 'results']),
    PreTaxMemberContributions() {
      return this.inferred[`PreTaxMemberContributions${this.modifier}`];
    },
    PreTaxMemberContributionsFrequency() {
      return this.inferred[`PreTaxMemberContributionsFrequency${this.modifier}`];
    },
    PostTaxMemberContributions() {
      return this.inferred[`PostTaxMemberContributions${this.modifier}`];
    },
    PostTaxMemberContributionsFrequency() {
      return this.inferred[`PostTaxMemberContributionsFrequency${this.modifier}`];
    },
    OneOffContribution() {
      return this.inputs[`OneOffContribution${this.modifier}`];
    },
    OneOffContributionMadeAtAge() {
      return this.inputs[`OneOffContributionMadeAtAge${this.modifier}`];
    },
    preTaxError() {
      // Additional before tax contribution alert.
      // Show if Additional before-tax contribution + member % of salary contribution (pre-tax) + employer contribution rate > concessional cap (annual not bring forward)
      // const age = this.inputs[`AgeNow${this.modifier}`].value;
      const salary = this.inputs[`Salary${this.modifier}`].value;
      const preTax = this.PreTaxMemberContributions.value;
      const memberContribution = (this.inputs[`PostTaxContributions${this.modifier}`].value ? 0 : this.inferred[`MemberContributions${this.modifier}`].value) * salary;
      const employerContribution = this.inputs[`EmployerContributionRate${this.modifier}`].value * salary;
      const preTaxContributions = preTax + memberContribution + employerContribution;
      const cap = this.results.parameters.concessionalContributionThreshold();

      return preTaxContributions > cap;
    },
    oneOffAgeError() {
      const age = this.inputs[`AgeNow${this.modifier}`].value;
      return age >= 65 || (this.OneOffContribution.value > 0 && this.OneOffContributionMadeAtAge.value >= 65);
    },
    postTaxErrors() {
      return this.postTaxError || this.coContributionError;
    },
    postTaxErrorMessages() {
      let message = '';

      if (this.postTaxError) message += '<p class="additional">Additional tax is paid on contributions that exceed the non-concessional contributions cap. These contributions will be taxed at your marginal rate, plus an interest charge. Please see our website for more info.</p>';
      if (this.coContributionError) message += '<p class="additional">If you contribute an after-tax contribution, you may qualify for extra contributions from the Government. Please see the qualifying criteria on our website for all the rules.</p>';

      return message;
    },
    postTaxError() {
      // Additional after tax contribution alert. (Non-concessional)
      // Show if Additional after-tax contribution + member % of salary contribution (post-tax) > non-concessional cap (annual not bring forward)
      const age = this.inputs[`AgeNow${this.modifier}`].value;
      const salary = this.inputs[`Salary${this.modifier}`].value;
      const postTax = this.PostTaxMemberContributions.value;
      const memberContribution = (this.inputs[`PostTaxContributions${this.modifier}`].value ? this.inferred[`MemberContributions${this.modifier}`].value : 0) * salary;
      const oneOffContribution = this.OneOffContributionMadeAtAge.value === age ? this.OneOffContribution.value : 0;
      const postTaxContributions = postTax + memberContribution + oneOffContribution;
      const cap = this.results.parameters.nonConcessionalContributionThreshold();

      return postTaxContributions > cap;
    },
    coContributionError() {
      const salary = this.inputs[`Salary${this.modifier}`].value;
      const employerType = this.inferred[`EmployerType${this.modifier}`].value;
      /* eslint-disable no-underscore-dangle */
      return salary < this.results.parameters._params.CoContributionUpperThreshold && employerType !== EmployerType.SELF_OR_NOT_EMPLOYED;
      /* eslint-enable no-underscore-dangle */
    },
  },
  methods: {
    ...mapActions([
      'actionPreTaxMemberContributions',
      'actionPreTaxMemberContributionsFrequency',
      'actionPostTaxMemberContributions',
      'actionPostTaxMemberContributionsFrequency',
      'actionOneOffContribution',
      'actionOneOffContributionMadeAtAge',
    ]),
    setPreTaxMemberContributions(value) {
      this.actionPreTaxMemberContributions({ value, modifier: this.modifier });
    },
    setPreTaxMemberContributionsFrequency(value) {
      this.actionPreTaxMemberContributionsFrequency({ value, modifier: this.modifier });
    },
    setPostTaxMemberContributions(value) {
      this.actionPostTaxMemberContributions({ value, modifier: this.modifier });
    },
    setPostTaxMemberContributionsFrequency(value) {
      this.actionPostTaxMemberContributionsFrequency({ value, modifier: this.modifier });
    },
    setOneOffContribution(value) {
      this.actionOneOffContribution({ value, modifier: this.modifier });
    },
    setOneOffContributionMadeAtAge(value) {
      this.actionOneOffContributionMadeAtAge({ value, modifier: this.modifier });
    },
  },
};
</script>
